import { Flex, Text } from '@servicetitan/anvil2';
import { Fieldset, Checkbox } from '@servicetitan/anvil2-react-hook-form';
import { SetupDataStore, useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { useLegalDisclosure } from '@servicetitan/financing-utils-shared';
import { usePersonalInfoFields } from './usePersonalInfoFields';
import { cloneElement, Fragment, type ReactNode } from 'react';

interface PersonalInformationFieldsProps {
    fieldPrefix?: string;
    /** @deprecated This prop is no longer used - field visibility is controlled by provider config */
    additionalApplicantFieldsOverride?: string[];
}

export const PersonalInformationFields = ({ fieldPrefix = '' }: PersonalInformationFieldsProps) => {
    const config = useProviderConfig();
    const { setupData } = SetupDataStore.useStore();
    const { fieldOrder, fieldRegistry } = usePersonalInfoFields();

    // Fetch legal disclosures once for the entire form
    const phoneDisclosure = useLegalDisclosure({
        providerName: setupData?.provider,
        tenantId: setupData?.tenantId,
        category: config.phoneDisclosureCategory,
    }) as string;

    const emailDisclosure = useLegalDisclosure({
        providerName: setupData?.provider,
        tenantId: setupData?.tenantId,
        category: config.emailDisclosureCategory,
    }) as string;

    const disclosures = { phone: phoneDisclosure, email: emailDisclosure };

    // Special handling for firstName/middleInitial side-by-side layout
    const renderFields = () => {
        const fields: ReactNode[] = [];
        let i = 0;

        while (i < fieldOrder.length) {
            const fieldKey = fieldOrder[i];
            const field = fieldRegistry[fieldKey];

            if (!field) {
                i++;
                continue;
            }

            // Check if we have email, primaryPhone, and secondaryPhone - render them side-by-side
            if (
                fieldKey === 'email' &&
                i + 2 < fieldOrder.length &&
                fieldOrder[i + 1] === 'primaryPhone' &&
                fieldOrder[i + 2] === 'secondaryPhone'
            ) {
                const emailField = fieldRegistry.email;
                const primaryPhoneField = fieldRegistry.primaryPhone;
                const secondaryPhoneField = fieldRegistry.secondaryPhone;

                if (emailField && primaryPhoneField && secondaryPhoneField) {
                    const emailComponent = emailField.component(fieldPrefix, config);
                    const primaryPhoneComponent = primaryPhoneField.component(fieldPrefix, config);
                    const secondaryPhoneComponent = secondaryPhoneField.component(
                        fieldPrefix,
                        config,
                    );

                    // Get descriptions for each field if they exist
                    const emailDescription = emailField.getDescription?.(config, disclosures);
                    const primaryPhoneDescription = primaryPhoneField.getDescription?.(
                        config,
                        disclosures,
                    );
                    const secondaryPhoneDescription = secondaryPhoneField.getDescription?.(
                        config,
                        disclosures,
                    );

                    fields.push(
                        <Flex
                            key="email-primaryPhone-secondaryPhone"
                            gap="4"
                            direction="column"
                            sm={{ direction: 'row' }}
                            style={{ width: '100%', gridColumn: '1 / -1' }}
                        >
                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                {cloneElement(emailComponent, {
                                    description: emailDescription,
                                })}
                            </div>
                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                <Flex direction="column" gap="2">
                                    {cloneElement(primaryPhoneComponent, {
                                        description: undefined,
                                    })}
                                    {primaryPhoneDescription &&
                                        config.phoneDisclosureCategory === 'phoneInfoButton' && (
                                            <Checkbox
                                                name={[fieldPrefix, 'phoneConsent'].join('.')}
                                                label={
                                                    <Text
                                                        variant="body"
                                                        size="small"
                                                        subdued
                                                        dangerouslySetInnerHTML={{
                                                            // eslint-disable-next-line @typescript-eslint/naming-convention
                                                            __html: primaryPhoneDescription,
                                                        }}
                                                    />
                                                }
                                            />
                                        )}
                                    {primaryPhoneDescription &&
                                        config.phoneDisclosureCategory !== 'phoneInfoButton' && (
                                            <Text variant="body" size="small" subdued>
                                                {primaryPhoneDescription}
                                            </Text>
                                        )}
                                </Flex>
                            </div>
                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                {cloneElement(secondaryPhoneComponent, {
                                    description: secondaryPhoneDescription,
                                })}
                            </div>
                        </Flex>,
                    );
                    i += 3; // Skip all three fields
                    continue;
                }
            }

            // Check if we have firstName followed by middleInitial - render them side-by-side
            if (
                fieldKey === 'firstName' &&
                i + 1 < fieldOrder.length &&
                fieldOrder[i + 1] === 'middleInitial'
            ) {
                const firstNameField = fieldRegistry.firstName;
                const middleInitialField = fieldRegistry.middleInitial;

                if (firstNameField && middleInitialField) {
                    const firstNameComponent = firstNameField.component(fieldPrefix, config);
                    const middleInitialComponent = middleInitialField.component(
                        fieldPrefix,
                        config,
                    );

                    fields.push(
                        <Flex key="firstName-middleInitial" style={{ width: '100%' }} gap="4">
                            {cloneElement(firstNameComponent, {
                                style: { width: 'calc(50% - 0.5rem)' },
                            })}
                            {cloneElement(middleInitialComponent, {
                                style: { width: 'calc(50% - 0.5rem)' },
                            })}
                        </Flex>,
                    );
                    i += 2; // Skip both fields
                    continue;
                }
            }

            // Regular field rendering
            const component = field.component(fieldPrefix, config);

            // If field has getDescription, inject it via cloneElement
            if (field.getDescription) {
                const description = field.getDescription(config, disclosures);

                if (
                    fieldKey === 'primaryPhone' &&
                    description &&
                    config.phoneDisclosureCategory === 'phoneInfoButton'
                ) {
                    fields.push(
                        <Fragment key={fieldKey}>
                            <Flex direction="column" gap="2">
                                {cloneElement(component, { description: undefined })}
                                <Checkbox
                                    name={[fieldPrefix, 'phoneConsent'].join('.')}
                                    label={
                                        <Text
                                            variant="body"
                                            size="small"
                                            subdued
                                            dangerouslySetInnerHTML={{
                                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                                __html: description,
                                            }}
                                        />
                                    }
                                />
                            </Flex>
                        </Fragment>,
                    );
                } else {
                    fields.push(
                        <Fragment key={fieldKey}>
                            {cloneElement(component, { description })}
                        </Fragment>,
                    );
                }
            } else {
                fields.push(<Fragment key={fieldKey}>{component}</Fragment>);
            }

            i++;
        }

        return fields;
    };

    return <Fieldset legend="Personal Information">{renderFields()}</Fieldset>;
};
